import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  increment,

} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '@festpix/services';

export interface SKU {
  id: string;
  boothId: string;
  name: string;
  shortName: string;
  price: number;
  quantity: number;
}

export interface CartItem {
  skuId: string;
  quantity: number;
}

export interface CartItemWithSKU extends CartItem {
  sku: SKU;
}

export interface TransactionItem {
  skuId: string;
  name: string;
  shortName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
}

export interface Booth {
  id: string;
  name: string;
  mainWalletId: string;
  vendorIds: string[];
}

interface QRCodeData {
  walletId: string;
}

/**
 * Get current user info for transaction logging
 */
async function getCurrentUserInfo(): Promise<{ id: string; email: string; name?: string }> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuário não autenticado');
  }

  const userDoc = await getDoc(doc(db, 'users', user.uid));
  const userData = userDoc.data();

  return {
    id: user.uid,
    email: user.email || '',
    name: userData?.name,
  };
}

/**
 * Get the booth for the current vendor
 */
export async function getVendorBooth(vendorId: string): Promise<Booth | null> {
  const boothsQuery = query(
    collection(db, 'booths'),
    where('vendorIds', 'array-contains', vendorId)
  );

  const snapshot = await getDocs(boothsQuery);

  if (snapshot.empty) {
    return null;
  }

  const boothDoc = snapshot.docs[0];
  const data = boothDoc.data();

  return {
    id: boothDoc.id,
    name: data.name,
    mainWalletId: data.mainWalletId,
    vendorIds: data.vendorIds || [],
  };
}

/**
 * Get all SKUs for a specific booth
 */
export async function getBoothSKUs(boothId: string): Promise<SKU[]> {
  const skusQuery = query(
    collection(db, 'skus'),
    where('boothId', '==', boothId),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(skusQuery);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    boothId: doc.data().boothId,
    name: doc.data().name,
    shortName: doc.data().shortName,
    price: doc.data().price,
    quantity: doc.data().quantity,
  }));
}

/**
 * Charge an attendee's wallet with cart items (vendor operation)
 * Transfers funds from attendee wallet to booth wallet and decrements inventory
 */
export async function chargeWalletWithCart(
  qrCodeData: string,
  cartItems: CartItem[],
  vendorId: string
): Promise<{ success: boolean; transactionId: string }> {
  if (!cartItems || cartItems.length === 0) {
    throw new Error('Carrinho vazio');
  }

  // Parse QR code data
  let parsedData: QRCodeData;
  try {
    parsedData = JSON.parse(qrCodeData);
  } catch {
    throw new Error('QR Code inválido');
  }

  const { walletId: attendeeWalletId } = parsedData;

  // Get vendor's booth
  const booth = await getVendorBooth(vendorId);
  if (!booth) {
    throw new Error('Vendedor não está associado a nenhum estande');
  }

  const vendorWalletId = booth.mainWalletId;
  const boothName = booth.name || 'Estande';

  if (!vendorWalletId) {
    throw new Error('Estande não possui carteira configurada');
  }

  // Get vendor info for logging
  const vendorInfo = await getCurrentUserInfo();

  // Execute transaction atomically
  const transactionId = await runTransaction(db, async (transaction) => {
    // Read all SKU documents
    const skuRefs = cartItems.map((item) => doc(db, 'skus', item.skuId));
    const skuDocs = await Promise.all(skuRefs.map((ref) => transaction.get(ref)));

    // Validate all SKUs and build transaction items
    const transactionItems: TransactionItem[] = [];
    let totalAmount = 0;

    for (let i = 0; i < skuDocs.length; i++) {
      const skuDoc = skuDocs[i];
      const cartItem = cartItems[i];

      if (!skuDoc.exists()) {
        throw new Error(`Produto não encontrado: ${cartItem.skuId}`);
      }

      const skuData = skuDoc.data()!;

      // Verify SKU belongs to vendor's booth
      if (skuData.boothId !== booth.id) {
        throw new Error(`Produto ${skuData.name} não pertence a este estande`);
      }

      // Check inventory
      if (skuData.quantity < cartItem.quantity) {
        throw new Error(
          `Estoque insuficiente para ${skuData.name}. Disponível: ${skuData.quantity}, Solicitado: ${cartItem.quantity}`
        );
      }

      const itemTotal = skuData.price * cartItem.quantity;
      totalAmount += itemTotal;

      transactionItems.push({
        skuId: cartItem.skuId,
        name: skuData.name,
        shortName: skuData.shortName,
        quantity: cartItem.quantity,
        unitPrice: skuData.price,
        totalAmount: itemTotal,
      });
    }

    // Read wallets
    const attendeeWalletRef = doc(db, 'wallets', attendeeWalletId);
    const vendorWalletRef = doc(db, 'wallets', vendorWalletId);

    const attendeeWallet = await transaction.get(attendeeWalletRef);
    const vendorWallet = await transaction.get(vendorWalletRef);

    if (!attendeeWallet.exists()) {
      throw new Error('Carteira do cliente não encontrada');
    }

    if (!vendorWallet.exists()) {
      throw new Error('Carteira do estande não encontrada');
    }

    const attendeeBalance = attendeeWallet.data()?.balance || 0;

    if (attendeeBalance < totalAmount) {
      throw new Error(
        `Saldo insuficiente. Necessário: R$ ${totalAmount.toFixed(2)}, Disponível: R$ ${attendeeBalance.toFixed(2)}`
      );
    }

    // Decrement all SKU quantities and increment sales counters
    for (let i = 0; i < skuRefs.length; i++) {
      const skuData = skuDocs[i].data()!;

      transaction.update(skuRefs[i], {
        quantity: increment(-cartItems[i].quantity),
        updatedAt: serverTimestamp(),
      });

      // Atomically increment sku_sales_counters (FR-BACK-17)
      const counterRef = doc(db, 'sku_sales_counters', cartItems[i].skuId);
      transaction.set(counterRef, {
        boothId: booth.id,
        skuName: skuData.name,
        salesCount: increment(cartItems[i].quantity),
      }, { merge: true });
    }

    // Update wallet balances
    transaction.update(attendeeWalletRef, {
      balance: increment(-totalAmount),
    });

    transaction.update(vendorWalletRef, {
      balance: increment(totalAmount),
    });

    // Create transaction record with itemized list
    const transactionRef = doc(collection(db, 'transactions'));
    transaction.set(transactionRef, {
      amount: totalAmount,
      fromWalletId: attendeeWalletId,
      toWalletId: vendorWalletId,
      timestamp: serverTimestamp(),
      type: 'purchase',
      vendorId,
      boothId: booth.id,
      boothName,
      items: transactionItems,
      performedBy: {
        id: vendorInfo.id,
        email: vendorInfo.email,
        name: vendorInfo.name,
      },
    });

    return transactionRef.id;
  });

  return { success: true, transactionId };
}

/**
 * Charge an attendee's wallet with a manual amount (fallback when no SKUs)
 */
export async function chargeWallet(
  qrCodeData: string,
  amount: number,
  vendorId: string
): Promise<{ success: boolean; transactionId: string }> {
  // Parse QR code data
  let parsedData: QRCodeData;
  try {
    parsedData = JSON.parse(qrCodeData);
  } catch {
    throw new Error('QR Code inválido');
  }

  const { walletId: attendeeWalletId } = parsedData;

  // Get vendor's booth
  const booth = await getVendorBooth(vendorId);
  if (!booth) {
    throw new Error('Vendedor não está associado a nenhum estande');
  }

  const vendorWalletId = booth.mainWalletId;
  const boothName = booth.name || 'Estande';

  if (!vendorWalletId) {
    throw new Error('Estande não possui carteira configurada');
  }

  // Get vendor info for logging
  const vendorInfo = await getCurrentUserInfo();

  // Execute transaction
  const transactionId = await runTransaction(db, async (transaction) => {
    const attendeeWalletRef = doc(db, 'wallets', attendeeWalletId);
    const vendorWalletRef = doc(db, 'wallets', vendorWalletId);

    const attendeeWallet = await transaction.get(attendeeWalletRef);
    const vendorWallet = await transaction.get(vendorWalletRef);

    if (!attendeeWallet.exists()) {
      throw new Error('Carteira do cliente não encontrada');
    }

    if (!vendorWallet.exists()) {
      throw new Error('Carteira do estande não encontrada');
    }

    const attendeeBalance = attendeeWallet.data()?.balance || 0;

    if (attendeeBalance < amount) {
      throw new Error('Saldo insuficiente');
    }

    // Update balances
    transaction.update(attendeeWalletRef, {
      balance: increment(-amount),
    });

    transaction.update(vendorWalletRef, {
      balance: increment(amount),
    });

    // Create transaction record
    const transactionRef = doc(collection(db, 'transactions'));
    transaction.set(transactionRef, {
      amount,
      fromWalletId: attendeeWalletId,
      toWalletId: vendorWalletId,
      timestamp: serverTimestamp(),
      type: 'purchase',
      vendorId,
      boothId: booth.id,
      boothName,
      performedBy: {
        id: vendorInfo.id,
        email: vendorInfo.email,
        name: vendorInfo.name,
      },
    });

    return transactionRef.id;
  });

  return { success: true, transactionId };
}

/**
 * Transaction data with wallet owner info for display
 */
export interface BoothTransactionData {
  id: string;
  timestamp: any;
  type: string;
  amount: number;
  fromWalletId: string;
  toWalletId: string;
  boothName?: string;
  boothId?: string;
  walletOwnerName?: string;
  performedBy?: {
    id: string;
    email: string;
    name?: string;
  };
  items?: TransactionItem[];
  refundedAt?: any;
}

/**
 * Get all transactions for a specific booth with wallet owner names
 */
export async function getBoothTransactions(boothId: string): Promise<BoothTransactionData[]> {
  // Get booth's main wallet ID
  const boothDoc = await getDoc(doc(db, 'booths', boothId));
  if (!boothDoc.exists()) {
    throw new Error('Estande não encontrado');
  }
  const boothWalletId = boothDoc.data().mainWalletId;

  // Query transactions where the booth wallet is the receiver (purchases)
  const transactionsQuery = query(
    collection(db, 'transactions'),
    where('toWalletId', '==', boothWalletId),
    where('type', '==', 'purchase'),
    orderBy('timestamp', 'desc')
  );

  const snapshot = await getDocs(transactionsQuery);

  // Get all unique fromWalletIds to fetch owner names
  const walletIds = new Set<string>();
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.fromWalletId) {
      walletIds.add(data.fromWalletId);
    }
  });

  // Fetch wallet owner names
  const walletOwnerNames: Record<string, string> = {};
  for (const walletId of walletIds) {
    try {
      const walletDoc = await getDoc(doc(db, 'wallets', walletId));
      if (walletDoc.exists()) {
        const walletData = walletDoc.data();
        // Check if it's a child wallet
        if (walletData.childName) {
          walletOwnerNames[walletId] = walletData.childName;
        } else if (walletData.name) {
          walletOwnerNames[walletId] = walletData.name;
        } else if (walletData.ownerId) {
          // Fetch owner's name from users collection
          const userDoc = await getDoc(doc(db, 'users', walletData.ownerId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            walletOwnerNames[walletId] = userData.name || userData.email || 'Desconhecido';
          }
        }
      }
    } catch (error) {
      console.error(`Error fetching wallet ${walletId}:`, error);
    }
  }

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      timestamp: data.timestamp,
      type: data.type,
      amount: data.amount,
      fromWalletId: data.fromWalletId,
      toWalletId: data.toWalletId,
      boothName: data.boothName,
      boothId: data.boothId,
      walletOwnerName: walletOwnerNames[data.fromWalletId] || 'Desconhecido',
      performedBy: data.performedBy,
      items: data.items,
      refundedAt: data.refundedAt,
    };
  });
}

/**
 * Get all vendors associated with a booth
 */
export async function getBoothVendors(boothId: string): Promise<{ id: string; name: string; email: string }[]> {
  const boothDoc = await getDoc(doc(db, 'booths', boothId));
  if (!boothDoc.exists()) {
    return [];
  }

  const vendorIds = boothDoc.data().vendorIds || [];
  const vendors: { id: string; name: string; email: string }[] = [];

  for (const vendorId of vendorIds) {
    try {
      const userDoc = await getDoc(doc(db, 'users', vendorId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        vendors.push({
          id: vendorId,
          name: userData.name || userData.email?.split('@')[0] || 'Vendedor',
          email: userData.email || '',
        });
      }
    } catch (error) {
      console.error(`Error fetching vendor ${vendorId}:`, error);
    }
  }

  return vendors;
}

/**
 * Refund (estornar) a transaction - reverses a purchase
 * Creates a new refund transaction and returns funds to the attendee
 */
export async function refundTransaction(
  transactionId: string,
  vendorId: string
): Promise<{ success: boolean; refundTransactionId: string }> {
  // Get vendor info for logging
  const vendorInfo = await getCurrentUserInfo();

  // Get the original transaction
  const originalTxDoc = await getDoc(doc(db, 'transactions', transactionId));
  if (!originalTxDoc.exists()) {
    throw new Error('Transação não encontrada');
  }

  const originalTx = originalTxDoc.data();

  // Validate it's a purchase transaction
  if (originalTx.type !== 'purchase') {
    throw new Error('Apenas compras podem ser estornadas');
  }

  // Check if already refunded
  if (originalTx.refundedAt) {
    throw new Error('Esta transação já foi estornada');
  }

  // Get vendor's booth to validate permissions
  const booth = await getVendorBooth(vendorId);
  if (!booth) {
    throw new Error('Vendedor não está associado a nenhum estande');
  }

  // Validate transaction belongs to this booth
  if (originalTx.toWalletId !== booth.mainWalletId) {
    throw new Error('Esta transação não pertence ao seu estande');
  }

  const attendeeWalletId = originalTx.fromWalletId;
  const boothWalletId = originalTx.toWalletId;
  const refundAmount = originalTx.amount;

  // Execute refund transaction atomically
  const refundTransactionId = await runTransaction(db, async (transaction) => {
    // Read wallets
    const attendeeWalletRef = doc(db, 'wallets', attendeeWalletId);
    const boothWalletRef = doc(db, 'wallets', boothWalletId);

    const attendeeWallet = await transaction.get(attendeeWalletRef);
    const boothWallet = await transaction.get(boothWalletRef);

    if (!attendeeWallet.exists()) {
      throw new Error('Carteira do cliente não encontrada');
    }

    if (!boothWallet.exists()) {
      throw new Error('Carteira do estande não encontrada');
    }

    const boothBalance = boothWallet.data()?.balance || 0;

    // Check booth has sufficient balance for refund
    if (boothBalance < refundAmount) {
      throw new Error(
        `Saldo insuficiente no estande para estorno. Necessário: R$ ${refundAmount.toFixed(2)}, Disponível: R$ ${boothBalance.toFixed(2)}`
      );
    }

    // If original transaction had SKU items, restore inventory and decrement sales counters
    if (originalTx.items && originalTx.items.length > 0) {
      for (const item of originalTx.items) {
        const skuRef = doc(db, 'skus', item.skuId);
        const skuDoc = await transaction.get(skuRef);
        if (skuDoc.exists()) {
          transaction.update(skuRef, {
            quantity: increment(item.quantity),
            updatedAt: serverTimestamp(),
          });

          // Decrement sales counter on refund
          const counterRef = doc(db, 'sku_sales_counters', item.skuId);
          transaction.set(counterRef, {
            salesCount: increment(-item.quantity),
          }, { merge: true });
        }
      }
    }

    // Update wallet balances (reverse the original transaction)
    transaction.update(attendeeWalletRef, {
      balance: increment(refundAmount),
    });

    transaction.update(boothWalletRef, {
      balance: increment(-refundAmount),
    });

    // Mark original transaction as refunded
    const originalTxRef = doc(db, 'transactions', transactionId);
    transaction.update(originalTxRef, {
      refundedAt: serverTimestamp(),
      refundedBy: {
        id: vendorInfo.id,
        email: vendorInfo.email,
        name: vendorInfo.name,
      },
    });

    // Create refund transaction record
    const refundTxRef = doc(collection(db, 'transactions'));
    transaction.set(refundTxRef, {
      amount: refundAmount,
      fromWalletId: boothWalletId,
      toWalletId: attendeeWalletId,
      timestamp: serverTimestamp(),
      type: 'refund',
      vendorId,
      boothId: booth.id,
      boothName: booth.name,
      originalTransactionId: transactionId,
      items: originalTx.items || null,
      performedBy: {
        id: vendorInfo.id,
        email: vendorInfo.email,
        name: vendorInfo.name,
      },
    });

    return refundTxRef.id;
  });

  return { success: true, refundTransactionId };
}

/**
 * Get wallet owner info by scanning QR code data (for promote flow)
 */
export async function getWalletOwnerInfo(qrCodeData: string): Promise<{
  walletId: string;
  ownerId: string;
  ownerName: string;
  isChildWallet: boolean;
}> {
  const parsed = JSON.parse(qrCodeData);
  if (!parsed.walletId) throw new Error('QR Code invalido');

  const walletDoc = await getDoc(doc(db, 'wallets', parsed.walletId));
  if (!walletDoc.exists()) throw new Error('Carteira nao encontrada');

  const walletData = walletDoc.data();
  const isChildWallet = walletData.isChildWallet === true;

  if (isChildWallet) {
    return {
      walletId: parsed.walletId,
      ownerId: walletData.ownerId,
      ownerName: walletData.childName || 'Dependente',
      isChildWallet: true,
    };
  }

  const userDoc = await getDoc(doc(db, 'users', walletData.ownerId));
  const userData = userDoc.data();

  return {
    walletId: parsed.walletId,
    ownerId: walletData.ownerId,
    ownerName: userData?.name || userData?.email || 'Desconhecido',
    isChildWallet: false,
  };
}

/**
 * Promote an attendee to vendor via Cloud Function
 */
export async function promoteAttendeeToVendor(
  qrCodeData: string,
  boothId: string
): Promise<{ promotedUserId: string; promotedUserName: string }> {
  const promoteVendor = httpsCallable<
    { qrCodeData: string; boothId: string },
    { success: boolean; promotedUserId: string; promotedUserName: string }
  >(functions, 'promoteVendor');

  const result = await promoteVendor({ qrCodeData, boothId });
  return {
    promotedUserId: result.data.promotedUserId,
    promotedUserName: result.data.promotedUserName,
  };
}

/**
 * Resign as vendor (demote self to attendee) via Cloud Function
 */
export async function resignAsVendor(): Promise<void> {
  const resignVendorFn = httpsCallable<Record<string, never>, { success: boolean }>(
    functions,
    'resignVendor'
  );
  await resignVendorFn({});
}
